import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCircle, AlertCircle, X, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import api from '../utils/api';

const getNotificationLink = (n) => {
  const ctx = n.context || {};
  const e = n.event || '';
  if (e.startsWith('booking_')) return ctx.booking_code ? `/bookings?code=${encodeURIComponent(ctx.booking_code)}` : '/bookings';
  if (e.includes('closure')) return '/closures';
  if (e === 'invoice_created' || e.startsWith('payment_')) return '/invoices';
  if (e === 'review_received') return '/reviews';
  if (e.includes('property') || e.includes('partner')) return '/properties';
  return null;
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/notification-logs?limit=20', { skipRetry: true, timeout: 8000 });
      const logs = response.data;
      
      // Get unread count from localStorage
      const lastViewedTime = localStorage.getItem('last_viewed_notifications');
      const unread = logs.filter(log => {
        if (!lastViewedTime) return true;
        return new Date(log.sent_at) > new Date(lastViewedTime);
      });
      
      setNotifications(logs);
      setUnreadCount(unread.length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const handleBellClick = () => {
    setShowPopup(!showPopup);
    if (!showPopup) {
      // Mark as viewed
      localStorage.setItem('last_viewed_notifications', new Date().toISOString());
      setUnreadCount(0);
    }
  };

  const getEventIcon = (event) => {
    if (event.includes('created')) return '🆕';
    if (event.includes('cancelled')) return '❌';
    if (event.includes('assigned')) return '🏠';
    if (event.includes('closure')) return '🚫';
    if (event.includes('payment')) return '💰';
    return '🔔';
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Ora';
    if (diffMins < 60) return `${diffMins} min fa`;
    if (diffHours < 24) return `${diffHours} h fa`;
    if (diffDays < 7) return `${diffDays} giorni fa`;
    return date.toLocaleDateString('it-IT');
  };

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        onClick={handleBellClick}
        className="relative"
      data-testid="notification-bell-button"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <Badge 
            className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-red-500"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </Badge>
        )}
      </Button>

      {showPopup && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setShowPopup(false)} data-testid="notif-popup-backdrop" />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-2rem)] max-w-md max-h-[75vh] bg-white rounded-lg shadow-xl border z-50 overflow-hidden flex flex-col" data-testid="notif-popup-panel">
          {/* Header */}
          <div className="p-4 border-b flex justify-between items-center bg-slate-50">
            <h3 className="font-semibold text-lg">Notifiche</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowPopup(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <Bell className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Nessuna notifica</p>
              </div>
            ) : (
              notifications.map((notification) => {
                const link = getNotificationLink(notification);
                return (
                <div
                  key={notification.id}
                  onClick={() => { if (link) { setShowPopup(false); navigate(link); } }}
                  data-testid={`notif-item-${notification.id}`}
                  className={`p-4 border-b transition-colors ${link ? 'cursor-pointer hover:bg-blue-50 active:bg-blue-100' : 'hover:bg-slate-50'}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-2xl flex-shrink-0">
                      {getEventIcon(notification.event)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm mb-1">
                        {notification.title}
                      </p>
                      <p className="text-sm text-slate-600 mb-2">
                        {notification.body}
                      </p>
                      <div className="flex items-center justify-between text-xs text-slate-400">
                        <span>{formatTime(notification.sent_at)}</span>
                        {notification.success ? (
                          <CheckCircle className="h-3 w-3 text-green-500" />
                        ) : (
                          <AlertCircle className="h-3 w-3 text-red-500" />
                        )}
                      </div>
                    </div>
                    {link && <ChevronRight className="h-4 w-4 text-slate-300 flex-shrink-0 self-center" />}
                  </div>
                </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t bg-slate-50 text-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowPopup(false);
                window.location.href = '/notification-settings';
              }}
              className="text-xs"
            >
              Gestisci Notifiche
            </Button>
          </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
